/**
 * The imports list's filter state — the same five values whether they live in
 * component state or in the address bar.
 *
 * KEEP IN STEP with `features/company-lists/useCompanyListsFilters.ts`, its
 * Company Lists cut (`q` + `page` only): the two must change together on
 * `readPage` (lenient parse, garbage → 1), the `{ replace: true }` writes, and
 * "a filter write drops `page`".
 *
 * ## Why a hook and not two components
 *
 * `ImportsListView` has two hosts that disagree about exactly one thing —
 * whether the reading position should survive leaving the list:
 *
 *   - `/research/imports` — filter, open a contact list, come back, and the
 *     filter is still there. That is what this hook's `persist` branch is for.
 *   - Search Detail's People Lists tab — `SearchDetailPage` renders its tabs
 *     as a ternary chain so the inactive ones UNMOUNT and their state is
 *     discarded, deliberately: what that page persists is the TAB, not the
 *     reading position inside it. `SearchDetailPage.test.tsx` pins it.
 *
 * So persistence is opt-in per host and its DEFAULT IS TODAY'S BEHAVIOUR. That
 * is not just politeness to the other host — it is the regression proof. Every
 * pre-existing `ImportsListView` test passes no flag, so if the whole suite
 * stays green untouched, the shared surface genuinely did not move.
 *
 * ## Why the setters are named for intent
 *
 * This is the part that is not obvious, and it is load-bearing.
 * `useSearchParams` closes over `searchParams` FROM THE CURRENT RENDER and
 * writes the whole query string in one `navigate` — including in its functional
 * form, which is handed `new URLSearchParams(searchParams)` from that same
 * closure. **Two writes in one tick therefore clobber each other; the second
 * wins outright.**
 *
 * The code this replaced did exactly that, three times:
 *
 *     setQ(searchText); setPage(1);        // debounce
 *     setOwner(next);   setPage(1);        // scope menu
 *     setTags(...);     setPage(1);        // tag chips
 *
 * Harmless against `useState` (React batches and both land); silently lossy
 * against the URL (the filter or the page reset disappears, depending on
 * order). Rather than leave that trap for the next reader, every caller-facing
 * operation here is ONE write that already includes its page reset. There is
 * deliberately no bare `setPage` in the returned API — `goToPage` is the only
 * way to move the page without resetting it, and it is named so that reaching
 * for it to "also" reset something reads wrong.
 *
 * ## The URL vocabulary
 *
 * Params are the bare wire names (`owner`, `tag`, `q`, `page`) so the address
 * bar and the network tab read alike — they mirror `ListImportsParams` in
 * `useImportsList.ts`. A DEFAULT VALUE IS THE ABSENCE OF THE PARAM, the
 * convention the Search Detail candidates table set (now `CandidatesTab`), and
 * every read is tolerant: an
 * unrecognised value reads as its default rather than as an error.
 *
 * `owner` is the ONE exception and `writeOwner` carries the reason: its default
 * is host-derived rather than constant, so an absent `owner` does not mean the
 * same thing on every mount. It is always written.
 *
 * Every write copies the CURRENT params and mutates the copy, so
 * `search_id_thrive`, `search_name` and `?preview=` (which every e2e spec
 * carries) survive untouched. Writes are always `{ replace: true }`: a
 * keystroke or a chip toggle is a filter tweak, not a navigation, and pushing
 * one per word would bury the page the reader arrived from. That is also what
 * makes browser Back work for free — the entry the reader leaves when they open
 * a contact list already carries the params.
 */
import type { ImportTag } from "@nu-assist/contracts";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { TYPE_TAGS } from "./tagDefs";

const OWNER_PARAM = "owner";
const TAG_PARAM = "tag";
const Q_PARAM = "q";
const PAGE_PARAM = "page";

export interface ImportsListFilters {
  /** `you` | `all` | an email — the wire values `router.py` validates. */
  owner: string;
  /** Multi-select with OR semantics; empty is the "All" chip. */
  tags: ImportTag[];
  /** The raw input. Never reaches the wire or the URL — `q` does. */
  searchText: string;
  /** The debounced, committed search term. */
  q: string;
  page: number;

  /** Draft only: responsive typing, no write anywhere. */
  setSearchText: (next: string) => void;
  /** Commit the debounced term. One write; resets the page. */
  commitQ: (next: string) => void;
  /** One write; resets the page. */
  setOwner: (next: string) => void;
  /** `null` is the "All" chip — clears the whole selection. One write; resets the page. */
  toggleTag: (next: ImportTag | null) => void;
  /** The pager and the clamp. One write; KEEPS the page, obviously. */
  goToPage: (next: number) => void;
  /**
   * The pinned search filter changed under a mounted view. Resets the page and,
   * when `widen` is true, moves a `you` scope to `all`. ONE write, because the
   * two used to be two `setState` calls and would clobber in URL mode.
   */
  onSearchFilterChanged: (widen: boolean) => void;
}

export interface UseImportsListFiltersArgs {
  /** Keep the filters in the URL. `false` (the default) is today's behaviour. */
  persist: boolean;
  /** The scope to start at, and — in URL mode — the value `?owner` omits. */
  initialOwner: string;
}

/**
 * `?page=abc`, `?page=0`, `?page=-3` all read as 1.
 *
 * `Number.isSafeInteger` is the third guard and it is not paranoia:
 * `parseInt("99999999999999999999")` is a finite `1e20`, so it survives the
 * `|| 1` and the `Math.max`, and reaches the wire. The page clamp downstream
 * only rescues the reader when the request SUCCEEDS — it is gated on
 * `query.isSuccess` — so an API that rejects the value instead leaves them in
 * an error state with no in-app way back. Cheaper to refuse it here.
 */
function readPage(params: URLSearchParams): number {
  const parsed = Number.parseInt(params.get(PAGE_PARAM) ?? "1", 10);
  if (!Number.isSafeInteger(parsed)) return 1;
  return Math.max(1, parsed);
}

/**
 * The type tags only (`raw | bct | ai`), never the full `ImportTag` union.
 *
 * `enriched` / `exported` / `combined` are legitimate WIRE values but stopped
 * being offered as chips in Trello #711 cl.1 — the toolbar renders
 * `FILTER_CHIPS`, which is `TYPE_TAGS`. Honouring them from the URL would
 * narrow the table with no chip lit to say why, which is worse than ignoring a
 * hand-typed param. Deduped, because `?tag=bct&tag=bct` is a link somebody can
 * write and a doubled wire value is a cache key nobody else shares.
 */
function readTags(params: URLSearchParams): ImportTag[] {
  const seen = new Set<ImportTag>();
  for (const raw of params.getAll(TAG_PARAM)) {
    const value = raw.trim();
    // Widen `TYPE_TAGS` to `string[]` rather than casting `value` to
    // `ImportTag`: the cast would be what makes `.includes` typecheck, which is
    // the check asserting the very thing it is supposed to prove. Widening the
    // haystack narrows the needle honestly.
    if ((TYPE_TAGS as readonly string[]).includes(value)) seen.add(value as ImportTag);
  }
  return [...seen];
}

/**
 * Write `owner` ALWAYS — the one param that does not follow "a default is the
 * absence of the param", and the exception is load-bearing.
 *
 * Every other default here is a constant (`page` 1, no tags, blank `q`), so an
 * absent param means the same thing on every mount. `owner`'s fallback is
 * `initialOwner`, which `ImportsPage` derives from whether `search_id_thrive`
 * is in the URL — a value that itself lives in the persisted query string. So
 * an omitted `owner` means "you" or "all" depending on how the reader got
 * there, and the round trip this whole feature exists for is exactly what
 * changes it:
 *
 *   1. land on /research/imports          -> initialOwner "you"
 *   2. apply a search from the toolbar    -> arrival widens to owner=all
 *   3. pick "Me" from the scope menu      -> omitted, because it equals... nothing;
 *                                            `initialOwner` is still "you"
 *   4. open a list, come Back             -> remount WITH search_id_thrive,
 *                                            so initialOwner is now "all"
 *   5. absent `owner` now reads back as "all" — the reader's Me is gone.
 *
 * Writing it always costs one always-present param and buys a URL that says
 * what it means. `initialOwner` keeps its real job: the value a reader who has
 * never touched the scope menu starts at.
 */
function writeOwner(params: URLSearchParams, value: string): void {
  params.set(OWNER_PARAM, value);
}

function writeTags(params: URLSearchParams, values: readonly ImportTag[]): void {
  params.delete(TAG_PARAM);
  for (const tag of values) params.append(TAG_PARAM, tag);
}

export function useImportsListFilters({
  persist,
  initialOwner,
}: UseImportsListFiltersArgs): ImportsListFilters {
  // Both branches are declared unconditionally — hooks cannot be conditional,
  // and both hosts are already under a Router (the test harness uses
  // `MemoryRouter`), so `useSearchParams` is always safe to call here. Only the
  // READS and WRITES branch.
  const [searchParams, setSearchParams] = useSearchParams();

  const [localOwner, setLocalOwner] = useState(initialOwner);
  const [localTags, setLocalTags] = useState<ImportTag[]>([]);
  const [localQ, setLocalQ] = useState("");
  const [localPage, setLocalPage] = useState(1);

  // The draft is local in BOTH modes: typing has to stay responsive, and the
  // committed value is what the debounce writes 300 ms later.
  //
  // SEEDED FROM THE URL, and that is not a nicety — an empty draft beside a
  // committed `q` is actively destructive. `ImportsListView`'s debounce guard
  // is `if (searchText === q) return`, so a mount at `?q=acme` fails the guard
  // 300 ms later and commits the EMPTY draft: `?q` is deleted, and because a
  // filter write also resets the page, `?page` goes with it. The reader would
  // watch a filtered list they arrived at silently reset to an unfiltered page
  // one — with the search box blank beside it the whole time, because
  // `ImportsToolbar` renders `value={searchText}`.
  //
  // Lazy initialiser, so it reads the params ONCE at mount. Re-seeding on every
  // render would fight the reader's typing (`q` lags the draft by the debounce
  // by design), and re-seeding on `q` changes would undo a keystroke landing in
  // the same tick as a commit.
  const [searchText, setSearchText] = useState(() =>
    persist ? (searchParams.get(Q_PARAM)?.trim() ?? "") : ""
  );

  const owner = persist ? (searchParams.get(OWNER_PARAM)?.trim() || initialOwner) : localOwner;
  const q = persist ? (searchParams.get(Q_PARAM)?.trim() ?? "") : localQ;
  const page = persist ? readPage(searchParams) : localPage;

  const urlTags = useMemo(() => readTags(searchParams), [searchParams]);
  const tags = persist ? urlTags : localTags;

  /**
   * Everything the write path needs, re-pointed every render and read at CALL
   * time. This is what makes every operation below referentially stable, and
   * that stability is not a micro-optimisation — it is a correctness fix.
   *
   * `setSearchParams` is NOT stable across renders: it is memoised on
   * `location.search`, so it changes identity on every navigation. A
   * `useCallback` that lists it therefore changes too, and so does every
   * operation built on it. Two of those operations are named in EFFECT
   * DEPENDENCY ARRAYS — the search-filter effect and the page clamp — so an
   * unstable identity re-runs effects that are supposed to fire only when the
   * filter or the page count moves.
   *
   * Measured, not theorised: clicking to page 2 wrote `?page=2`, the arrival
   * effect re-ran purely because its callback had a new identity, and its write
   * (which resets the page, as every filter write does) deleted the param again
   * — the pager snapped back to 1. Reading through a ref keeps the callbacks
   * `[]`-stable while still using the FRESHEST setter, which matters because
   * react-router hands even the functional form a copy of the params from its
   * own closure rather than from live history.
   *
   * Re-pointed in a LAYOUT effect, not during render. Writing a ref while
   * rendering is the rule React asks you not to break — a render that is
   * discarded (concurrent, or a throw) would leave the ref describing a commit
   * that never happened. A layout effect runs before the passive effects and
   * before any event handler of that commit, which is every reader here, so the
   * value is just as fresh with none of that exposure.
   */
  const latest = useRef({ setSearchParams, persist, initialOwner });
  useLayoutEffect(() => {
    latest.current = { setSearchParams, persist, initialOwner };
  });

  /**
   * The single write primitive. `keepPage` is the exception, not the rule:
   * changing any filter invalidates the page number, because page 9 of a set
   * that now has 12 rows is an empty table beside a pager claiming otherwise.
   *
   * NO no-op short-circuit, deliberately. An earlier version returned `current`
   * unchanged when the serialized query was identical, claiming it avoided a
   * navigation. It does not: react-router 7.15.0's `setSearchParams` calls
   * `navigate("?" + createSearchParams(nextInit(...)))` UNCONDITIONALLY
   * (`react-router/dist/development/chunk-5KNZJZUH.mjs`), so returning
   * `current` only hands it an object that serializes to the same string. Nor
   * does a no-op write churn identity the way that comment claimed —
   * `searchParams` is memoised on `location.search`, which is unchanged. The
   * guard was inert and its reasoning was wrong in both directions, which is
   * worse than absent: a future reader would have trusted it. If skipping the
   * navigation ever matters, the comparison has to happen at the CALL SITE,
   * before `setSearchParams` is reached.
   */
  const update = useCallback(
    (mutate: (params: URLSearchParams) => void, options: { keepPage?: boolean } = {}) => {
      latest.current.setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutate(next);
          if (!options.keepPage) next.delete(PAGE_PARAM);
          return next;
        },
        { replace: true }
      );
    },
    []
  );

  const commitQ = useCallback(
    (next: string) => {
      // Trimmed in BOTH branches, so the two stores hold the same value.
      //
      // NOT a wire fix, and an earlier version of this comment claimed it was.
      // `toListParams` already does `args.q.trim()` before building the request
      // and `toListQueryKey` reads that trimmed value, so a padded term has
      // always reached the wire — and the cache — trimmed, on both hosts,
      // before and after this change. Review raised it as a divergence that
      // changed Search Detail's requests; checking the wire path showed it did
      // not, and there is deliberately no local-branch test because there is
      // nothing observable to pin.
      //
      // Worth keeping anyway: the divergence was real in the STORED value, and
      // it is one new consumer away from mattering — anything reading `q` that
      // does not trim for itself would see two different strings depending on
      // which host it is under.
      const trimmed = next.trim();
      if (!latest.current.persist) {
        setLocalQ(trimmed);
        setLocalPage(1);
        return;
      }
      update((params) => {
        if (trimmed) params.set(Q_PARAM, trimmed);
        else params.delete(Q_PARAM);
      });
    },
    [update]
  );

  const setOwner = useCallback(
    (next: string) => {
      if (!latest.current.persist) {
        setLocalOwner(next);
        setLocalPage(1);
        return;
      }
      update((params) => writeOwner(params, next));
    },
    [update]
  );

  const toggleTag = useCallback(
    (next: ImportTag | null) => {
      const toggle = (prev: readonly ImportTag[]): ImportTag[] => {
        if (next === null) return [];
        return prev.includes(next) ? prev.filter((t) => t !== next) : [...prev, next];
      };
      if (!latest.current.persist) {
        setLocalTags(toggle);
        setLocalPage(1);
        return;
      }
      // Read the tags back out of `params` rather than off the render's `tags`:
      // this callback runs inside the updater, so `params` is the freshest set,
      // and the toggle is arithmetic on it rather than on a snapshot.
      update((params) => writeTags(params, toggle(readTags(params))));
    },
    [update]
  );

  const goToPage = useCallback(
    (next: number) => {
      // Clamped in BOTH branches, for the same reason `commitQ` trims in both:
      // `goToPage(0)` was storable as 0 in local mode and read back as 1 in URL
      // mode, and a seam whose two sides disagree on an edge is how the next
      // divergence gets in.
      const target = Math.max(1, next);
      if (!latest.current.persist) {
        setLocalPage(target);
        return;
      }
      update(
        (params) => {
          if (target <= 1) params.delete(PAGE_PARAM);
          else params.set(PAGE_PARAM, String(target));
        },
        { keepPage: true }
      );
    },
    [update]
  );

  const onSearchFilterChanged = useCallback(
    (widen: boolean) => {
      if (!latest.current.persist) {
        setLocalPage(1);
        // Functional, so it reads the scope at apply time rather than
        // capturing it — same reason the effect that calls this always was.
        if (widen) setLocalOwner((prev) => (prev === "you" ? "all" : prev));
        return;
      }
      update((params) => {
        if (!widen) return;
        const current = params.get(OWNER_PARAM)?.trim() || latest.current.initialOwner;
        if (current === "you") writeOwner(params, "all");
      });
    },
    [update]
  );

  return {
    owner,
    tags,
    searchText,
    q,
    page,
    setSearchText,
    commitQ,
    setOwner,
    toggleTag,
    goToPage,
    onSearchFilterChanged,
  };
}
